import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, lazy, Suspense } from 'react';

import { queryClient } from '@/api/queryClient';
import { useAuthStore } from '@/contexts/authStore';
import GlobalUpgradeModal from '@/components/modules/GlobalUpgradeModal';
import { initGTM, trackPageView } from '@/lib/analytics';

// Layouts and Guards (keep eager - needed for route structure)
import DashboardLayout from '@/components/modules/DashboardLayout';
import ProtectedRoute from '@/components/modules/ProtectedRoute';
import GuestRoute from '@/components/modules/GuestRoute';

// Pages - Lazy loaded for code splitting
const Landing = lazy(() => import('@/pages/Landing'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Inspirations = lazy(() => import('@/pages/Inspirations'));
const Studio = lazy(() => import('@/pages/Studio'));
const Document = lazy(() => import('@/pages/Document'));
const Settings = lazy(() => import('@/pages/Settings'));
const CreativeDNA = lazy(() => import('@/pages/CreativeDNA'));
const Prompts = lazy(() => import('@/pages/Prompts'));
const ProjectOverview = lazy(() => import('@/pages/ProjectOverview'));
const OfferLinkGenerator = lazy(() => import('@/pages/OfferLinkGenerator'));

// Loading fallback for lazy components
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#FAFAF9]">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );
}

// JWT auth initializer - checks stored token on mount
function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    // Initialize GTM once on app startup
    initGTM();
    initialize();
  }, [initialize]);

  return <>{children}</>;
}

// Page view tracker - tracks route changes
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

// Smart redirect: logged users → dashboard, others → landing
function SmartRedirect() {
  const { user, loading } = useAuthStore();

  if (loading) {
    return null;
  }

  return <Navigate to={user ? '/dashboard' : '/'} replace />;
}

// Redirect /offer to /register preserving query params
function OfferRedirect() {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  return <Navigate to={`/register${queryString ? `?${queryString}` : ''}`} replace />;
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <PageTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/offer" element={<OfferRedirect />} />

                {/* Guest-only Routes (redirect to dashboard if logged in) */}
                <Route element={<GuestRoute />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                </Route>

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  {/* Protected route without DashboardLayout (no sidebar) */}
                  <Route path="/offer-link-generator" element={<OfferLinkGenerator />} />

                  <Route element={<DashboardLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/dashboard/creative-dna" element={<CreativeDNA />} />
                    <Route
                      path="/dashboard/project/:projectId"
                      element={<Navigate to="overview" replace />}
                    />
                    <Route
                      path="/dashboard/project/:projectId/overview"
                      element={<ProjectOverview />}
                    />
                    <Route
                      path="/dashboard/project/:projectId/inspirations"
                      element={<Inspirations />}
                    />
                    <Route path="/dashboard/project/:projectId/studio" element={<Studio />} />
                    <Route
                      path="/dashboard/project/:projectId/studio/document/:documentId"
                      element={<Document />}
                    />
                    <Route path="/dashboard/project/:projectId/settings" element={<Settings />} />
                    <Route
                      path="/dashboard/project/:projectId/creative-dna"
                      element={<CreativeDNA />}
                    />
                    <Route
                      path="/dashboard/project/:projectId/prompts"
                      element={<Prompts />}
                    />
                  </Route>
                </Route>

                {/* Catch-all: logged users go to dashboard, others to landing */}
                <Route path="*" element={<SmartRedirect />} />
              </Routes>
            </Suspense>

            {/* Global Upgrade Modal - triggers on 402 errors */}
            <GlobalUpgradeModal />
          </BrowserRouter>

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1E293B',
                color: '#fff',
                borderRadius: '12px',
                padding: '12px 16px',
              },
              success: {
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </AuthProvider>

        {/* React Query Devtools (only in development) */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
